import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

export default function DriverDropdown({
  drivers = [],
  value,
  onChange,
  placeholder = "Select Driver First",
  loading = false,
  className = "",
  dropdownHeight = "max-h-60",
  placement = "bottom", // "bottom" | "top"
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDriverId = (driver) =>
    String(
      driver?.driverId ||
        driver?.driver_id ||
        driver?.driver_code ||
        driver?.id ||
        "",
    ).trim();

  const getDriverName = (driver) =>
    driver?.fullName || driver?.name || driver?.username || "Driver";

  const filteredDrivers = useMemo(() => {
    return (drivers || []).filter((driver) => {
      const id = getDriverId(driver);
      const name = getDriverName(driver);

      return `${id} ${name}`.toLowerCase().includes(search.toLowerCase());
    });
  }, [drivers, search]);

  const selectedDriver = drivers.find(
    (driver) => getDriverId(driver) === String(value),
  );

  const isTop = placement === "top";

  return (
    <div ref={dropdownRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        disabled={loading}
        onClick={() => setOpen((prev) => !prev)}
        className="w-full h-10 rounded-lg border bg-white px-3 text-sm flex items-center justify-between"
      >
        <span className="truncate text-gray-600">
          {selectedDriver
            ? `${getDriverId(selectedDriver)} - ${getDriverName(selectedDriver)}`
            : placeholder}
        </span>

        <ChevronDown
          size={16}
          className={isTop ? "" : ""}
        />
      </button>

      {open && (
        <div
          className={`absolute left-0 w-full rounded-lg border bg-white shadow-lg z-50 ${
            isTop ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          <div className="flex items-center gap-2 border-b p-2">
            <Search size={15} />

            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search driver..."
              className="flex-1 text-sm outline-none"
            />
          </div>

          <div className={`${dropdownHeight} overflow-y-auto`}>
            <button
              className="w-full px-3 py-2 text-left hover:bg-gray-100 text-sm"
              onClick={() => {
                onChange("");
                setOpen(false);
                setSearch("");
              }}
            >
              {placeholder}
            </button>

            {filteredDrivers.map((driver) => (
              <button
                key={getDriverId(driver)}
                className="w-full px-3 py-2 text-left hover:bg-gray-100 text-sm"
                onClick={() => {
                  onChange(getDriverId(driver));
                  setOpen(false);
                  setSearch("");
                }}
              >
                {getDriverId(driver)} - {getDriverName(driver)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}